import React, { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { Color, deleteColor } from '~/services/MerchandiseService';

import EditColorDialog from './EditColorDialog';

interface Props {
  data: Array<Color>;
  isLoading: boolean;
}

const DEFAULT_COLUMN_PROPS: Partial<GridColDef> = {
  sortable: false,
  disableColumnMenu: true,
  flex: 1,
  display: 'flex',
};

const COLOR_PREVIEW_SIZE = 24;

function ColorsTable({ data, isLoading }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [editingColor, setEditingColor] = useState<Color | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const deleteColorMutation = useMutation({
    mutationFn: (colorName: string) => deleteColor(colorName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchandise', 'colors'] });
      enqueueSnackbar('Color deleted successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error deleting color: ${error.message}`, { variant: 'error' });
    },
  });

  const handleDelete = (colorName: string) => {
    if (window.confirm(`Are you sure you want to delete the color "${colorName}"?`)) {
      deleteColorMutation.mutate(colorName);
    }
  };

  const handleEdit = (row: Color) => {
    setEditingColor(row);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingColor(null);
  };

  const renderHexCell = (params: GridRenderCellParams<Color>) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: COLOR_PREVIEW_SIZE,
            height: COLOR_PREVIEW_SIZE,
            backgroundColor: params.value,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
          }}
        />
        <span>{params.value}</span>
      </Box>
    );
  };

  const renderActionsCell = (params: GridRenderCellParams<Color>) => {
    const isDeleting = deleteColorMutation.isPending;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleEdit(params.row)}
          disabled={isDeleting}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => handleDelete(params.row.name)}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </Box>
    );
  };

  return (
    <>
      <EditColorDialog open={isEditDialogOpen} color={editingColor} onClose={handleCloseEditDialog} />
      <Box mt={2}>
        <DataGrid
          columns={[
            {
              ...DEFAULT_COLUMN_PROPS,
              field: 'name',
              headerName: 'Name',
            },
            {
              ...DEFAULT_COLUMN_PROPS,
              field: 'parentColor',
              headerName: 'Parent',
            },
            {
              ...DEFAULT_COLUMN_PROPS,
              field: 'hex',
              headerName: 'Hex',
              renderCell: renderHexCell,
            },
            {
              ...DEFAULT_COLUMN_PROPS,
              field: 'actions',
              headerName: 'Actions',
              renderCell: renderActionsCell,
            },
          ]}
          rows={data}
          loading={isLoading}
          getRowId={(row: Color) => row.name}
          disableRowSelectionOnClick
          disableMultipleRowSelection
        />
      </Box>
    </>
  );
}

export default ColorsTable;
